import type { OrderSettlementDto } from '../../../dto/orderSettlement';
import type { Order } from '../../../types/order';
import {
  canShowOrderMarginPercent,
  getOrderMarginCostState,
  getOrderMarginSummary,
  type OrderMarginCostState,
} from '../orderMarginSummary';
import type { OrderCostPlanLine, OrderCostPlanPendingReason } from '../../../types/orderWarehouseSummary';
import type { OrderFifoPreviewLine } from '../api/orderProductsApi';

/**
 * The one rule of which order margin the ZK card shows and what it is called
 * (decision 2026-10-04 pkt 8: „marża ma się zgadzać i być wyjaśniona”).
 * Nothing is recomputed here — the numbers come from one backend source:
 *
 * - „Marża planowana”: the WP5 plan stored on the order (`order.margin`,
 *   `order.marginPercent`): issued FIFO + stock allocated in confirmation order
 *   + average purchase cost of the shortfall, plus outgoing transport and
 *   other order costs. Before every line is issued by WZ.
 * - „Marża po WZ”: the same stored plan once every line is issued — then the
 *   whole goods cost is the cost of the batches the WZ took.
 * - „Marża końcowa”: the order is closed by the sales invoice and the reader
 *   has the invoice ledger (`GET /orders/:id/settlement` → `actual`).
 *
 * `costIncomplete` keeps today's „z niepełnym kosztem” marker: part of the
 * cost is unknown (never zero), so the margin may be overstated.
 */
export type OrderMarginStage = 'PLANNED' | 'AFTER_WZ' | 'FINAL';

export interface OrderMarginIncompleteLine {
  name: string;
  /** Why the line has no cost (WP5 plan); null when the reader has only the cost-basis label. */
  reason: OrderCostPlanPendingReason | null;
}

export interface OrderMarginFigure {
  stage: OrderMarginStage;
  source: 'PLAN' | 'SETTLEMENT';
  /** PLN; null when the cost is unknown (`costState` UNKNOWN). */
  amount: number | null;
  /** Percent of revenue; only for a fully known cost, never „100%” of an unknown cost. */
  percent: number | null;
  /** The one cost rule (orderMarginSummary.getOrderMarginCostState). */
  costState: OrderMarginCostState;
  /** Lines without cost, named on the chip and the tiles („niepełny koszt: …”). */
  incompleteLines: OrderMarginIncompleteLine[];
  costIncomplete: boolean;
  /**
   * The cost is known but preliminary: batches received without a purchase
   * invoice (warehouse summary `costProvisional`). The card labels such a
   * margin „wstępna”, also when it is the final one. A ledger with
   * `costStatus` PENDING is `costIncomplete` instead.
   */
  provisional: boolean;
}

export const ORDER_MARGIN_STAGE_LABEL_KEYS: Record<OrderMarginStage, string> = {
  PLANNED: 'orders:process.margin.stage.PLANNED',
  AFTER_WZ: 'orders:process.margin.stage.AFTER_WZ',
  FINAL: 'orders:process.margin.stage.FINAL',
};

/**
 * Label of the plan ledger in „Szczegóły → Skąd się bierze marża”. After the
 * invoice that ledger is the plan frozen at the invoice, while the header
 * shows the ledger result — so it must not say „Planowana marża” there.
 */
export const ORDER_MARGIN_DETAILS_LABEL_KEYS: Record<OrderMarginStage, string> = {
  PLANNED: 'orders:warehouseFlow.plannedMargin',
  AFTER_WZ: 'orders:process.margin.stage.AFTER_WZ',
  FINAL: 'orders:process.margin.detailsFrozen',
};

type MarginOrder = Pick<Order, 'status' | 'revenue' | 'totalCost' | 'margin' | 'marginPercent' | 'warehouseCostPending'>;

export interface OrderMarginStageInput {
  order: MarginOrder;
  /** Invoice ledger; null for roles without it (or before it loaded). */
  settlement?: Pick<OrderSettlementDto, 'entries' | 'actual' | 'costStatus'> | null;
  /** Every order line is issued by WZ (stock-status lines all `ISSUED`). */
  allIssued: boolean;
  /** Warehouse summary `finance.costProvisional` (full-margin readers only). */
  costProvisional?: boolean;
  /** Lines without cost (`getOrderMarginIncompleteLines`). */
  incompleteLines?: OrderMarginIncompleteLine[];
}

/**
 * The order lines whose cost is unknown, by name: from the WP5 plan when the
 * reader has it (with the reason), otherwise from the per-line cost basis of
 * the planned-margin preview (Handlowiec). Nothing is recomputed.
 */
export function getOrderMarginIncompleteLines(input: {
  products: Array<{ id: string; name: string }>;
  planLines?: Pick<OrderCostPlanLine, 'id' | 'costPending' | 'pendingReason'>[] | null;
  fifoLines?: Pick<OrderFifoPreviewLine, 'id' | 'costBasis' | 'pendingReason'>[] | null;
  displayName?: (product: { id: string; name: string }) => string;
}): OrderMarginIncompleteLine[] {
  const name = (id: string) => {
    const product = input.products.find((row) => row.id === id);
    return product ? (input.displayName ? input.displayName(product) : product.name) : null;
  };
  if (input.planLines && input.planLines.length > 0) {
    return input.planLines
      .filter((line) => line.costPending)
      .map((line) => ({ name: name(line.id), reason: line.pendingReason }))
      .filter((line): line is OrderMarginIncompleteLine => line.name !== null);
  }
  return (input.fifoLines ?? [])
    .filter((line) => line.costBasis === 'UNKNOWN')
    .map((line) => ({ name: name(line.id), reason: line.pendingReason ?? null }))
    .filter((line): line is OrderMarginIncompleteLine => line.name !== null);
}

export function getOrderMarginStage({ order, settlement, allIssued }: OrderMarginStageInput): OrderMarginStage {
  if (order.status === 'INVOICED' && settlement && settlement.entries.length > 0) return 'FINAL';
  return allIssued ? 'AFTER_WZ' : 'PLANNED';
}

export function getOrderMarginFigure(input: OrderMarginStageInput): OrderMarginFigure {
  const stage = getOrderMarginStage(input);
  if (stage === 'FINAL' && input.settlement) {
    const { actual, costStatus } = input.settlement;
    // Ledger cost still waiting for a purchase invoice = incomplete; „wstępna”
    // only from the warehouse flag, so the card never shows both markers for one cause.
    const costState: OrderMarginCostState = actual.margin == null || actual.totalCost == null
      ? 'UNKNOWN'
      : costStatus === 'PENDING' ? 'PARTIAL' : 'KNOWN';
    return {
      stage,
      source: 'SETTLEMENT',
      amount: costState === 'UNKNOWN' ? null : actual.margin,
      percent: canShowOrderMarginPercent(costState) ? actual.marginPercent : null,
      costState,
      incompleteLines: costState === 'KNOWN' ? [] : (input.incompleteLines ?? []),
      costIncomplete: costState !== 'KNOWN',
      provisional: input.costProvisional === true,
    };
  }
  const { order } = input;
  const costState = getOrderMarginCostState(order);
  const summary = getOrderMarginSummary(order);
  return {
    stage,
    source: 'PLAN',
    amount: costState === 'UNKNOWN' ? null : summary.margin,
    percent: canShowOrderMarginPercent(costState) ? summary.percent : null,
    costState,
    incompleteLines: costState === 'KNOWN' ? [] : (input.incompleteLines ?? []),
    costIncomplete: costState !== 'KNOWN',
    provisional: input.costProvisional === true,
  };
}

/**
 * What a role may see of the margin (backend `financialPermissions` decides;
 * this mirrors it for the card). Full margin readers (ADMIN) and planned-margin
 * readers (Handlowiec on his own ZK) see amount and percent of the plan, as on
 * today's card; Operacje see no margin at all.
 */
export interface OrderMarginVisibility {
  showMargin: boolean;
  showAmount: boolean;
  /** Per-line cost amounts and the „Skąd koszt” batch costs. */
  showCosts: boolean;
}

export function getOrderMarginVisibility(caps: {
  canViewMargin: boolean;
  canViewPlannedMargin: boolean;
}): OrderMarginVisibility {
  const showMargin = caps.canViewMargin || caps.canViewPlannedMargin;
  return { showMargin, showAmount: showMargin, showCosts: caps.canViewMargin };
}
